// Local-first sick-weeks hook.
//
// Sick weeks are an idempotent toggle on the server: POST {habit_id, week_start}
// flips the bit and the response carries the resulting {sick}. The local-first
// flow stores only "true" rows in IDB (toggle-off = delete the row) and treats
// the in-memory map as a set: `sickWeeks[key]` is truthy iff that habit is
// resting for that week. Toggles enqueue to the outbox; background fetches
// reconcile within the requested window, preserving any rows whose habit is
// still an unsynced temp id.

import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchWithRetry } from '../utils/fetchWithRetry';
import { formatLocalDate, getWeekStart } from '../utils/dateHelpers';
import { logger } from '../utils/logger';
import { useConnectivity } from '../context/ConnectivityContext';
import {
  bulkPut,
  del as idbDel,
  getAll as idbGetAll,
  getRangeByIndex,
  put as idbPut,
  sickWeekKey,
} from '../offline/db';
import { enqueue, isTempId } from '../offline/outbox';

const log = logger.child('useSickWeeks');

const STORE = 'sickWeeks';

const rowsToMap = (rows) => {
  const m = {};
  for (const r of rows) m[r.key] = true;
  return m;
};

export const useSickWeeks = (apiUrl, user, currentWeekStart) => {
  const [sickWeeks, setSickWeeks] = useState({});
  const activeFetchKeys = useRef(new Set());
  const { notifyEnqueued, registerIdResolver, registerRejectionHandler } = useConnectivity();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cached = await idbGetAll(STORE);
        if (!cancelled && cached.length > 0) setSickWeeks(rowsToMap(cached));
      } catch (e) {
        log.warn('hydrate from idb failed', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchSickWeeks = useCallback(async (weekStart, force = false) => {
    if (!user || !weekStart) return;
    const requestKey = `${weekStart.getTime()}`;
    if (!force && activeFetchKeys.current.has(requestKey)) return;
    activeFetchKeys.current.add(requestKey);

    const startDate = new Date(weekStart);
    startDate.setDate(startDate.getDate() - 365);
    const endDate = new Date(weekStart);
    endDate.setDate(endDate.getDate() + 7);
    const startStr = formatLocalDate(startDate);
    const endStr = formatLocalDate(endDate);

    try {
      const res = await fetchWithRetry(
        `${apiUrl}/api/sick-weeks?start=${startStr}&end=${endStr}`,
        { headers: { 'Authorization': `Bearer ${user.token}` }, cache: 'no-cache' }
      );
      if (!res.ok) throw new Error('Failed to fetch sick weeks');
      const data = await res.json();

      const serverRows = [];
      for (const sw of data) {
        const rawDate = sw.week_start;
        const dateStr = typeof rawDate === 'string'
          ? rawDate.split('T')[0]
          : formatLocalDate(new Date(rawDate));
        const habitId = sw.habit_id ?? sw.habitId ?? sw.habit;
        if (!habitId || !dateStr) continue;
        serverRows.push({ key: sickWeekKey(habitId, dateStr), habit_id: habitId, week_start: dateStr });
      }

      // Reconcile the window: drop IDB rows in [start,end] that the server
      // didn't return (server is authoritative for synced habits), but keep
      // any row whose habit_id is still an unsynced temp id — those will be
      // rewritten by the idResolver when the habit's create resolves.
      const inWindow = await getRangeByIndex(STORE, 'week_start', startStr, endStr);
      const serverKeys = new Set(serverRows.map((r) => r.key));
      const stale = inWindow.filter((r) => !serverKeys.has(r.key) && !isTempId(r.habit_id));
      for (const r of stale) await idbDel(STORE, r.key);
      if (serverRows.length > 0) await bulkPut(STORE, serverRows);

      const all = await idbGetAll(STORE);
      setSickWeeks(rowsToMap(all));
    } catch (error) {
      log.error('fetchSickWeeks failed', error, { method: 'GET', url: `${apiUrl}/api/sick-weeks`, start: startStr, end: endStr });
    } finally {
      activeFetchKeys.current.delete(requestKey);
    }
  }, [user, apiUrl]);

  const toggleSickWeek = useCallback(async (habitId, weekStart) => {
    const monday = weekStart instanceof Date ? getWeekStart(weekStart) : getWeekStart(new Date(weekStart));
    const weekStartStr = formatLocalDate(monday);
    const key = sickWeekKey(habitId, weekStartStr);
    const wasSick = !!sickWeeks[key];
    const nextSick = !wasSick;

    setSickWeeks((prev) => {
      const copy = { ...prev };
      if (nextSick) copy[key] = true;
      else delete copy[key];
      return copy;
    });

    try {
      if (nextSick) {
        await idbPut(STORE, { key, habit_id: habitId, week_start: weekStartStr });
      } else {
        await idbDel(STORE, key);
      }
      await enqueue({
        type: 'sickWeek.toggle',
        payload: { habit_id: habitId, week_start: weekStartStr },
        habitRef: isTempId(habitId) ? habitId : null,
      });
      notifyEnqueued();
    } catch (e) {
      log.error('toggleSickWeek enqueue failed', e, { habitId, weekStartStr });
      setSickWeeks((prev) => {
        const copy = { ...prev };
        if (wasSick) copy[key] = true;
        else delete copy[key];
        return copy;
      });
    }
  }, [sickWeeks, notifyEnqueued]);

  const isHabitSick = useCallback((habitId, weekStart) => {
    const monday = weekStart instanceof Date ? getWeekStart(weekStart) : getWeekStart(new Date(weekStart));
    return !!sickWeeks[sickWeekKey(habitId, formatLocalDate(monday))];
  }, [sickWeeks]);

  // When a temp habit gets a real id, rewrite every sickWeek row whose
  // habit_id matches: delete old key, insert new key, update React state.
  useEffect(() => {
    return registerIdResolver(async (tempId, realId) => {
      try {
        const all = await idbGetAll(STORE);
        const affected = all.filter((r) => r.habit_id === tempId);
        if (affected.length === 0) return;
        for (const r of affected) await idbDel(STORE, r.key);
        const rewritten = affected.map((r) => ({
          key: sickWeekKey(realId, r.week_start),
          habit_id: realId,
          week_start: r.week_start,
        }));
        await bulkPut(STORE, rewritten);
        const fresh = await idbGetAll(STORE);
        setSickWeeks(rowsToMap(fresh));
      } catch (e) {
        log.warn('idResolver cascade failed', e, { tempId, realId });
      }
    });
  }, [registerIdResolver]);

  // Roll back local state on permanent rejection of a sickWeek.toggle.
  useEffect(() => {
    return registerRejectionHandler(async (row, status) => {
      if (row.type !== 'sickWeek.toggle') return;
      const { habit_id, week_start } = row.payload;
      const key = sickWeekKey(habit_id, week_start);
      const cached = await idbGetAll(STORE);
      const present = cached.some((r) => r.key === key);
      // Our local state holds the optimistic post-toggle value; reverting
      // means flipping it back to the pre-toggle value, which is what
      // *would* be present after one more toggle. Simplest: re-toggle locally.
      setSickWeeks((prev) => {
        const copy = { ...prev };
        if (copy[key]) delete copy[key]; else copy[key] = true;
        return copy;
      });
      if (present) {
        try { await idbDel(STORE, key); } catch (_) { /* ignore */ }
      } else {
        try { await idbPut(STORE, { key, habit_id, week_start }); } catch (_) { /* ignore */ }
      }
      log.warn('sickWeek.toggle rejected; reverted local', { habit_id, week_start, status });
    });
  }, [registerRejectionHandler]);

  useEffect(() => {
    if (currentWeekStart && user) fetchSickWeeks(currentWeekStart);
  }, [currentWeekStart, user, fetchSickWeeks]);

  return {
    sickWeeks,
    setSickWeeks,
    fetchSickWeeks,
    toggleSickWeek,
    isHabitSick,
  };
};
